(() => {
  if (window.xoaCartNote) return;

  // One ordered save queue for the drawer and cart page. It survives section
  // replacement and never lets an older request overwrite a newer draft.
  const store = window.xoaCartNote = {
    initialized: false,
    draft: '',
    saved: '',
    status: '',
    pending: null,
    elements: new Set(),
    get dirty() { return this.draft !== this.saved; },
    render() {
      this.elements.forEach((element) => element.render());
      document.querySelectorAll('.cart__dynamic-checkout-buttons').forEach((element) => {
        element.inert = this.dirty || Boolean(this.pending) || this.status === 'error';
        element.setAttribute('aria-busy', String(element.inert));
      });
    },
    update(value) {
      this.draft = value;
      this.status = this.dirty ? 'saving' : 'saved';
      clearTimeout(this.timer);
      this.render();
      this.timer = setTimeout(() => this.flush().catch(() => {}), 300);
    },
    flush() {
      clearTimeout(this.timer);
      if (this.pending) return this.pending;
      if (!this.dirty) { this.status = 'saved'; this.render(); return Promise.resolve(); }
      this.pending = (async () => {
        try {
          while (this.dirty) {
            const value = this.draft;
            this.status = 'saving';
            this.render();
            const response = await fetch(routes.cart_update_url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ note: value }),
            });
            if (!response.ok) throw new Error('Cart note save failed');
            const cart = await response.json();
            if ((cart.note || '') !== value) throw new Error('Cart note was not saved');
            this.saved = value;
          }
          this.status = 'saved';
        } catch (error) {
          this.status = 'error';
          throw error;
        } finally {
          this.pending = null;
          this.render();
        }
      })();
      this.render();
      return this.pending;
    },
  };

  customElements.define('cart-note', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('textarea');
      if (!this.input) return;
      if (!store.initialized) {
        store.draft = store.saved = this.input.value;
        store.initialized = true;
      }
      store.elements.add(this);
      this.abort = new AbortController();
      this.input.addEventListener('input', () => store.update(this.input.value), { signal: this.abort.signal });
      this.querySelector('[data-note-retry]')?.addEventListener('click', () => store.flush().catch(() => {}), { signal: this.abort.signal });
      store.render();
    }
    disconnectedCallback() {
      this.abort?.abort();
      store.elements.delete(this);
    }
    render() {
      if (this.input.value !== store.draft) this.input.value = store.draft;
      const status = this.querySelector('[data-note-status]');
      if (status) {
        status.textContent = this.dataset[store.status] || '';
        status.setAttribute('role', store.status === 'error' ? 'alert' : 'status');
      }
      const retry = this.querySelector('[data-note-retry]');
      if (retry) retry.hidden = store.status !== 'error';
    }
  });

  let checkoutPending = false;
  const permitted = new WeakSet();
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!['cart', 'CartDrawer-Form'].includes(form.id) || event.submitter?.name !== 'checkout') return;
    if (permitted.has(form)) { permitted.delete(form); return; }
    if (!store.dirty && !store.pending && store.status !== 'error') return;
    event.preventDefault();
    if (checkoutPending) return;
    checkoutPending = true;
    const button = event.submitter;
    button.setAttribute('aria-busy', 'true');
    try {
      await store.flush();
      // A cart quantity response can replace the checkout form while saving.
      const currentForm = document.getElementById(form.id);
      const currentButton = document.getElementById(button.id);
      if (currentForm && currentButton && !currentButton.disabled) {
        permitted.add(currentForm);
        currentForm.requestSubmit(currentButton);
      }
    } catch (_) {
      document.querySelector(`cart-note textarea[form="${form.id}"]`)?.focus();
    } finally {
      checkoutPending = false;
      button.removeAttribute('aria-busy');
    }
  }, true);
})();
